/**
 * Modal for creating a new chat channel.
 */

import React, { useState } from 'react';
import {
  Modal,
  ModalDialog,
  ModalClose,
  Typography,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Checkbox,
  Button,
  Stack,
  Alert,
} from '@mui/joy';
import { useCreateChannel } from '../../hooks/useChat';

interface CreateChannelModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: (channelId: string) => void;
}

const CreateChannelModal: React.FC<CreateChannelModalProps> = ({
  open,
  onClose,
  onSuccess,
}) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isPrivate, setIsPrivate] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const createChannel = useCreateChannel();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!name.trim()) {
      setError('Channel name is required');
      return;
    }

    try {
      const result = await createChannel.mutateAsync({
        name: name.trim(),
        description: description.trim() || undefined,
        is_private: isPrivate,
      });

      // Reset form
      setName('');
      setDescription('');
      setIsPrivate(false);

      onClose();
      onSuccess?.(result.id);
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create channel';
      setError(errorMessage);
    }
  };

  const handleClose = () => {
    setName('');
    setDescription('');
    setIsPrivate(false);
    setError(null);
    onClose();
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <ModalDialog sx={{ minWidth: 400 }}>
        <ModalClose />
        <Typography level="h4" sx={{ mb: 2 }}>
          Create Channel
        </Typography>

        <form onSubmit={handleSubmit}>
          <Stack spacing={2}>
            {error && (
              <Alert color="danger" variant="soft">
                {error}
              </Alert>
            )}

            <FormControl required>
              <FormLabel>Channel Name</FormLabel>
              <Input
                placeholder="e.g., general, announcements"
                value={name}
                onChange={(e) => setName(e.target.value)}
                autoFocus
              />
            </FormControl>

            <FormControl>
              <FormLabel>Description</FormLabel>
              <Textarea
                placeholder="What's this channel about?"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                minRows={2}
              />
            </FormControl>

            <FormControl>
              <Checkbox
                label="Make this channel private"
                checked={isPrivate}
                onChange={(e) => setIsPrivate(e.target.checked)}
              />
              <Typography level="body-xs" sx={{ color: 'text.secondary', ml: 4 }}>
                Private channels can only be joined by invitation
              </Typography>
            </FormControl>

            <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 2 }}>
              <Button variant="plain" color="neutral" onClick={handleClose}>
                Cancel
              </Button>
              <Button
                type="submit"
                loading={createChannel.isPending}
                disabled={!name.trim()}
              >
                Create Channel
              </Button>
            </Stack>
          </Stack>
        </form>
      </ModalDialog>
    </Modal>
  );
};

export default CreateChannelModal;
